import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUsers } from "./hooks/useUsers";
import { apiService } from "./services/apiService";
import { AdminLayout } from "./components/layout/AdminLayout";
import { Badge } from "./components/ui/Badge";
import { Dropdown } from "./components/ui/Dropdown";
import { Spinner } from "./components/ui/Spinner";
import { ErrorState } from "./components/ui/ErrorState";
import { EmptyState } from "./components/ui/EmptyState";
import { DateBadge } from "./components/ui/DateBadge";
import { UserAvatar } from "./components/ui/UserAvatar";
import { EditUserModal } from "./components/ui/EditUserModal";
import { Toast } from "./components/ui/Toast";

interface UserItem {
  id: string;
  name: string;
  email: string;
  trialStartedAt: string;
  trialEndDate: string;
  activationDate: string | null;
  status: "Active" | "Inactive";
}

const UserManagement = () => {
  const navigate = useNavigate();
  const { users, setUsers, loading, error } = useUsers();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [dateType, setDateType] = useState("Trial Start");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserItem | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  // Route protection
  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem("adminLoggedIn");
    if (isLoggedIn !== "true") {
      navigate("/admin/login");
    }
  }, [navigate]);

  // Filtering users based on search term, status dropdown, and date range
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "All Statuses" ||
      user.status.toLowerCase() === statusFilter.toLowerCase();

    let matchesDate = true;
    let dateValStr: string | null = null;

    if (dateType === "Trial Start") {
      dateValStr = user.trialStartedAt;
    } else if (dateType === "Trial End") {
      dateValStr = user.trialEndDate;
    } else if (dateType === "Activation Date") {
      dateValStr = user.activationDate;
    }

    if (dateValStr) {
      const userDate = new Date(dateValStr);

      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (userDate < start) matchesDate = false;
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (userDate > end) matchesDate = false;
      }
    } else if (startDate || endDate) {
      // If a date filter is actively set but the user does not have this date property (e.g. no activationDate), exclude them
      matchesDate = false;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleSaveUser = async (updatedData: {
    trialStartedAt: string;
    trialEndDate: string;
    status: "Active" | "Inactive";
  }) => {
    if (!selectedUser) return;
    try {
      const res = await apiService.put<{
        success: boolean;
        message?: string;
        user?: UserItem;
      }>(`portal-api/users/${selectedUser.id}`, updatedData);

      if (res.success && res.user) {
        // Optimistic / Instant local state update
        const updatedUser = res.user;
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.id === selectedUser.id ? { ...u, ...updatedUser } : u,
          ),
        );

        // Close the modal
        setSelectedUser(null);

        // Show Success Toast
        setToast({ message: "User updated successfully!", type: "success" });
      } else {
        throw new Error(res.message || "Failed to update user.");
      }
    } catch (err: any) {
      console.error("Save user error:", err);
      setToast({
        message: err.message || "Failed to update user.",
        type: "error",
      });
      throw err;
    }
  };

  return (
    <AdminLayout
      searchQuery={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder='Search users...'>
      {/* Header Row */}
      <div className='flex justify-between items-start mb-6'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 tracking-tight'>
            User Management
          </h1>
          <p className='text-sm text-gray-500 mt-1.5 font-medium'>
            Manage trials, subscriptions, and user access levels.
          </p>
        </div>

        {/* Table Filter Controls */}
        <div className='flex items-center gap-3'>
          {/* Date Type Selector Dropdown */}
          <Dropdown
            value={dateType}
            onChange={(e) => setDateType(e.target.value)}
            options={["Trial Start", "Trial End", "Activation Date"]}
          />

          {/* Date Range Selectors */}
          <div className='flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-2xl shadow-sm'>
            <div className='flex items-center gap-1'>
              <span className='text-[10px] font-bold text-gray-400 uppercase tracking-wider select-none'>
                From
              </span>
              <input
                type='date'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className='bg-transparent text-xs font-semibold text-gray-700 focus:outline-none cursor-pointer'
              />
            </div>
            <span className='text-gray-300'>|</span>
            <div className='flex items-center gap-1'>
              <span className='text-[10px] font-bold text-gray-400 uppercase tracking-wider select-none'>
                To
              </span>
              <input
                type='date'
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className='bg-transparent text-xs font-semibold text-gray-700 focus:outline-none cursor-pointer'
              />
            </div>
            {(startDate || endDate) && (
              <>
                <span className='text-gray-300'>|</span>
                <button
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                  className='text-xs text-red-500 hover:text-red-700 font-bold focus:outline-none px-1 py-0.5 hover:bg-red-50 rounded-lg transition-colors'>
                  Clear
                </button>
              </>
            )}
          </div>

          <Dropdown
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={["All Statuses", "Active", "Inactive"]}
          />
        </div>
      </div>

      {/* Main User Table Container */}
      <div className='bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden flex-1 flex flex-col'>
        {loading ? (
          <Spinner text='Loading registered users...' />
        ) : error ? (
          <ErrorState message={error} />
        ) : filteredUsers.length === 0 ? (
          <EmptyState />
        ) : (
          <div className='overflow-x-auto flex-1'>
            <table className='w-full text-left border-collapse'>
              <thead>
                <tr className='bg-gray-50 border-b border-gray-200'>
                  <th className='px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider'>
                    User
                  </th>
                  <th className='px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider'>
                    Trial Start
                  </th>
                  <th className='px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider'>
                    Trial End
                  </th>
                  <th className='px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider'>
                    Activation Date
                  </th>
                  <th className='px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider'>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    className='hover:bg-gray-50/50 transition-colors cursor-pointer'>
                    {/* USER Circle avatar + Details */}
                    <td className='px-6 py-4'>
                      <div className='flex items-center gap-3'>
                        <UserAvatar name={user.name} />
                        <div>
                          <div className='text-sm font-bold text-gray-900'>
                            {user.name}
                          </div>
                          <div className='text-xs text-gray-400 font-medium mt-0.5'>
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* TRIAL START */}
                    <td className='px-6 py-4'>
                      <DateBadge dateString={user.trialStartedAt} />
                    </td>

                    {/* TRIAL END */}
                    <td className='px-6 py-4'>
                      <DateBadge dateString={user.trialEndDate} />
                    </td>

                    {/* ACTIVATION DATE */}
                    <td className='px-6 py-4'>
                      <DateBadge dateString={user.activationDate} />
                    </td>

                    {/* STATUS Badge */}
                    <td className='px-6 py-4'>
                      <Badge status={user.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal Overlay */}
      {selectedUser && (
        <EditUserModal
          user={selectedUser}
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          onSave={handleSaveUser}
        />
      )}

      {/* Reusable Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminLayout>
  );
};

export default UserManagement;
